import {
  QUERY_KEY_MY_MONITORING_GET_SITE,
  QUERY_KEY_MY_MONITORING_GET_SITE_LIST,
  useMyMonitoringUpdateSiteDefault,
  useMyMonitoringUpdateSiteNotifications
} from '@apps-common/apis/my-monitoring/site'
import { FieldInfoMessage, ToggleInputSM } from '@apps-common/components/elements'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { useMyMonitoringNotificationsStore } from '@apps-common/stores'
import { IMMSiteModel, IMMSiteNotifications } from '@apps-common/types/my-monitoring'
import { Capacitor } from '@capacitor/core'
import classNames from 'classnames'
import { useState } from 'react'
import { SettingsModal } from '../settings/SettingsModal'

interface IProps {
  // open: boolean
  // close: () => void
  site?: IMMSiteModel
}

export const SiteSettingsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IProps
}) => {
  const { site } = componentProps

  const [isDefault, setIsDefault] = useState<boolean | undefined>(site?.isDefault)
  const { hydrated, notificationsEnabled } = useMyMonitoringNotificationsStore()

  const isNative = Capacitor.isNativePlatform()

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_SITE_LIST() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_SITE(site?.unitId) })
  }

  const { mutate: mutateSiteDefault, isPending: isPendingSiteDefault } = useMyMonitoringUpdateSiteDefault(false, (value: boolean) => {
    setIsDefault(value)
    invalidateQueries()
  })

  const { mutate: mutateSiteNotifications, isPending: isPendingSiteNotifications } = useMyMonitoringUpdateSiteNotifications(
    false,
    (value: IMMSiteNotifications) => {
      invalidateQueries()
    }
  )

  const pending = isPendingSiteDefault || isPendingSiteNotifications
  const globalNotificationsDisabled = !hydrated || notificationsEnabled !== true
  const shouldDisableOtherNotifications = !site?.receiveNotifications

  return (
    <ModalContainer innerClasses='sm:!h-auto sm:!min-h-0' onDismiss={onDismiss}>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle={site?.siteName || 'Site'} title='Settings' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[95px] sm:!pt-[90px] !pb-[30px]'>
        <ToggleInputSM
          labelLeft='Make this my default site'
          containerClassName='!mb-0 !px-0'
          innerContainerClassName='!m-0 !px-0'
          disabled={pending}
          registration={{
            checked: isDefault,
            onChange: (e: any) => {
              if (e?.target?.checked !== undefined) {
                mutateSiteDefault({ siteId: site?.unitId || '', isDefault: e?.target.checked })
              }
            }
          }}
        />
        <FieldInfoMessage message='If set, this will be your primary site and will be shown when the app first loads. If it’s your only site, this setting has no effect.' />
        <div className='border-b border-border mt-3 mb-4' />
        {globalNotificationsDisabled && (
          <div className='bg-warning text-on-warning px-2 py-2 rounded-2xl my-4'>
            <FieldInfoMessage
              classes='!text-on-warning/70'
              message='Notifications are disabled on this device. Even if site notifications are enabled, notifications will not be delivered to this device.'
            />
            <div className='flex items-center justify-end'>
              <ModalButton
                component={SettingsModal}
                disabled={false}
                componentProps={{}}
                enterAnimation={modalSlideUpEnterAnimation}
                leaveAnimation={modalSlideUpLeaveAnimation}
                backdropDismiss
              >
                <div className='text-[0.85rem] flex items-center justify-center gap-1 border border-on-warning px-2 py-[2px] rounded-full'>
                  Update Settings <commonIcons.arrowAngled />
                </div>
              </ModalButton>
            </div>
          </div>
        )}

        <ToggleInputSM
          labelLeft='Receive site notifications'
          containerClassName='!mb-0 !px-0'
          innerContainerClassName='!m-0 !px-0'
          disabled={pending}
          registration={{
            checked: site?.receiveNotifications || false,
            onChange: (e: any) => {
              if (e?.target?.checked !== undefined && !!site) {
                const value = e?.target.checked
                mutateSiteNotifications({
                  model: {
                    siteId: site?.unitId || '',
                    userId: 0,
                    receiveNotifications: value,
                    receiveOpenCloseNotifications: site.receiveOpenCloseNotifications,
                    receiveActivationNotifications: site.receiveActivationNotifications
                  }
                })
              }
            }
          }}
        />
        <FieldInfoMessage message='Disabling notifications will stop this site from sending notifications to any device on your account. This will also affect control status updates.' />
        <div className='mt-3 mb-4' />
        <div className={classNames('bg-skeleton px-3 py-4 rounded-2xl', shouldDisableOtherNotifications && 'opacity-70')}>
          {!site?.receiveNotifications && (
            <FieldInfoMessage message='To change open/close or activation notifications, first enable notifications above.' />
          )}
          <VertSpacerMd />
          <ToggleInputSM
            labelLeft='Open/Close Notifications'
            containerClassName='!mb-0 !px-0'
            innerContainerClassName='!m-0 !px-0'
            disabled={pending || shouldDisableOtherNotifications}
            registration={{
              checked: site?.receiveOpenCloseNotifications || false,
              onChange: (e: any) => {
                if (e?.target?.checked !== undefined && !!site) {
                  mutateSiteNotifications({
                    model: {
                      siteId: site?.unitId || '',
                      userId: 0,
                      receiveNotifications: site.receiveNotifications,
                      receiveOpenCloseNotifications: e?.target.checked,
                      receiveActivationNotifications: site.receiveActivationNotifications
                    }
                  })
                }
              }
            }}
          />
          <FieldInfoMessage message='Disabling open/close notifications will stop all open and close alerts for this site. This applies across both web and app.' />
          <VertSpacerMd />
          <ToggleInputSM
            labelLeft='Activation Notifications'
            containerClassName='!mb-0 !px-0'
            innerContainerClassName='!m-0 !px-0'
            disabled={pending || shouldDisableOtherNotifications}
            registration={{
              checked: site?.receiveActivationNotifications || false,
              onChange: (e: any) => {
                if (e?.target?.checked !== undefined && !!site) {
                  mutateSiteNotifications({
                    model: {
                      siteId: site?.unitId || '',
                      userId: 0,
                      receiveNotifications: site.receiveNotifications,
                      receiveOpenCloseNotifications: site.receiveOpenCloseNotifications,
                      receiveActivationNotifications: e?.target.checked
                    }
                  })
                }
              }
            }}
          />
          <FieldInfoMessage message='Disabling activation notifications will stop all activation alerts for this site. This applies across both web and app.' />
        </div>
      </ModalBody>
    </ModalContainer>
  )
}
